/**
 * Hallyu4U API Integration
 * Connects theme with backend server
 */

// Get API base URL based on environment
function getApiBaseUrl() {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8080";
  }
  return "https://hallyu4u.api.hallyu-manager.com";
}

// API Configuration
const HALLYU4U_API = {
  get baseUrl() {
    return getApiBaseUrl();
  },
  endpoints: {
    weBuyWeShip: "/api/orders",
    youBuyWeShip: "/api/orders",
    calculateShipping: "/api/shipping/calculate",
    trackPackage: "/api/packages/track",
    customerDashboard: "/api/customers/dashboard",
    secondPayment: "/api/payments/second",
    customerCode: "/api/customers/code",
    getCustomer: "/api/customers/shopify",
  },
};

// Store customer code globally
let customerCodeCache = null;

// We Buy We Ship Form Handler
function initWeBuyWeShip() {
  const form = document.getElementById("we-buy-form");
  if (!form) return;

  const successMessage = document.getElementById("form-success");
  const errorMessage = document.getElementById("form-error");
  const submitBtn = form.querySelector('button[type="submit"]');
  const btnText = submitBtn.querySelector(".btn-text");
  const btnLoading = submitBtn.querySelector(".btn-loading");

  form.addEventListener("submit", async function (e) {
    e.preventDefault();
    e.stopPropagation();

    // Check if image upload is in progress or required image is missing
    const imageContainers = form.querySelectorAll('.image-upload-container');
    for (const container of imageContainers) {
      if (container.dataset.uploading === 'true') {
        alert('Please wait for image upload to complete.');
        return;
      }
      if (container.dataset.required === 'true') {
        const urlInput = container.querySelector('input[type="hidden"]');
        if (!urlInput || !urlInput.value) {
          alert('Please upload the required product image.');
          return;
        }
      }
    }

    // Show loading state
    btnText.style.display = "none";
    btnLoading.style.display = "inline-flex";
    submitBtn.disabled = true;

    // Collect form data
    const formData = new FormData(form);

    // Get warehouse ID from display element
    const warehouseIdElement = document.getElementById("warehouse-id-value");
    const warehouseId = warehouseIdElement
      ? warehouseIdElement.textContent
      : "";

    const data = {
      orderType: "WE_BUY",
      items: [
        {
          productUrl: formData.get("productUrl"),
          productName: formData.get("productName"),
          productPrice: formData.get("productPrice"),
          priceCurrency: formData.get("priceCurrency"),
          quantity: formData.get("quantity"),
          size: formData.get("size"),
          color: formData.get("color"),
          productImageUrl: formData.get("productImageUrl"),
          content: formData.get("packageContents"),
        },
      ],
      customerInfo: {
        firstName: formData.get("firstName"),
        lastName: formData.get("lastName"),
        email: formData.get("email"),
        phone: formData.get("phone"),
        warehouseId: warehouseId,
      },
      notes: formData.get("specialInstructions") || "",
    };

    try {
      // Submit to backend API using API Store
      const apiResult = await window.Hallyu4UAPI.orders.submitWeBuy(data);

      // Parse response - handle both JSON and HTML
      let result = null;
      let responseData = apiResult.data;
      let responseText =
        typeof responseData === "string"
          ? responseData
          : JSON.stringify(responseData);

      // Check if response is HTML (from redirect)
      if (responseText.trim().startsWith("<")) {
        // Got HTML (likely from redirect) - treat as success regardless of status
        console.log("[We Buy] Received HTML response, treating as success");
        result = { data: { orderId: "WBWS-" + Date.now() } };
      } else {
        // Try to parse JSON
        try {
          result =
            typeof responseData === "object"
              ? responseData
              : JSON.parse(responseText);
        } catch (jsonError) {
          // If status is OK but JSON parse failed, treat as success
          if (apiResult.status === 200) {
            result = { data: { orderId: "WBWS-" + Date.now() } };
          } else {
            throw new Error("Invalid server response");
          }
        }
      }

      // Check result - new API returns ResponseDto<ProductOrderDto> with data field
      if (result && result.data) {
        const orderData = result.data;
        // Show success message
        form.style.display = "none";
        successMessage.style.display = "block";
        document.getElementById("request-id").textContent =
          orderData.orderId || orderData.id || "WBWS-" + Date.now();

        // Scroll to success message
        successMessage.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        // Handle error response - new API may return error in different format
        const errorMsg =
          result?.message ||
          result?.error ||
          result?.data?.message ||
          apiResult.error ||
          "Submission failed";
        throw new Error(errorMsg);
      }
    } catch (error) {
      // Check if it's a benign error (these are OK - submission likely succeeded)
      const isBenignError =
        error.message &&
        (error.message.includes("JSON") ||
          error.message.includes("parse") ||
          error.message.includes("Unexpected token") ||
          error.message.includes("Cannot read properties"));

      if (isBenignError) {
        // Benign error - submission likely succeeded, show success
        console.log(
          "[We Buy] Suppressing benign error, treating as success:",
          error.message
        );
        form.style.display = "none";
        successMessage.style.display = "block";
        document.getElementById("request-id").textContent =
          "WBWS-" + Date.now();
        successMessage.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        // Real error - hide form and success message, show error message only
        form.style.display = "none";
        successMessage.style.display = "none";

        const errorTextElement = document.getElementById("error-text");
        if (errorTextElement) {
          errorTextElement.textContent =
            error.message || "An error occurred. Please try again.";
        }
        errorMessage.style.display = "block";
        errorMessage.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } finally {
      // Reset button state
      btnText.style.display = "inline";
      btnLoading.style.display = "none";
      submitBtn.disabled = false;
    }
  });
}

// Function to handle payment button click
window.handlePaymentClick = async function (
  event,
  orderId,
  invoiceId,
  invoiceUrl
) {
  event.stopPropagation();

  // Show loading state on the button
  const button = event.target;
  const originalContent = button.innerHTML;
  button.innerHTML = "⏳ Loading...";
  button.disabled = true;

  try {
    if (invoiceUrl && invoiceUrl !== "null" && invoiceUrl !== "") {
      console.log(
        "✅ [Payment] Redirecting to Shopify admin invoice URL:",
        invoiceUrl
      );
      window.open(invoiceUrl, "_blank");
    } else if (invoiceId && invoiceId !== "null" && invoiceId !== "") {
      console.log("📋 [Payment] Fetching invoice for draft order:", invoiceId);

      // Use API Store for invoice request
      const apiResult = await window.Hallyu4UAPI.request(
        "/we-buy-we-ship/get-invoice",
        {
          method: "POST",
          body: {
            orderId: orderId,
            draftOrderId: invoiceId,
          },
          key: "weBuy.getInvoice",
        }
      );

      if (apiResult.success && apiResult.data) {
        const data =
          typeof apiResult.data === "object"
            ? apiResult.data
            : JSON.parse(apiResult.data);
        if (data.invoiceUrl) {
          console.log(
            "✅ [Payment] Got Shopify admin invoice URL:",
            data.invoiceUrl
          );
          window.open(data.invoiceUrl, "_blank");
        } else {
          // Fallback: Construct customer-facing invoice URL
          const checkoutUrl = `https://hallyu4u.myshopify.com/${invoiceId}/invoices/latest`;
          console.log("✅ [Payment] Using Shopify checkout URL:", checkoutUrl);
          window.open(checkoutUrl, "_blank");
        }
      } else {
        // If API fails, try direct Shopify invoice URL
        const checkoutUrl = `https://hallyu4u.myshopify.com/${invoiceId}/invoices/latest`;
        console.log("⚠️ [Payment] API failed, using direct URL:", checkoutUrl);
        window.open(checkoutUrl, "_blank");
      }
    } else {
      console.log(
        "🔍 [Payment] Checking for existing invoice from admin for order:",
        orderId
      );

      // Use API Store for invoice request
      const apiResult = await window.Hallyu4UAPI.request(
        "/we-buy-we-ship/get-invoice",
        {
          method: "POST",
          body: { orderId: orderId },
          key: "weBuy.getInvoice",
        }
      );

      if (apiResult.success && apiResult.data) {
        const data =
          typeof apiResult.data === "object"
            ? apiResult.data
            : JSON.parse(apiResult.data);
        if (data.invoiceUrl) {
          console.log(
            "✅ [Payment] Found existing admin invoice:",
            data.invoiceUrl
          );
          window.open(data.invoiceUrl, "_blank");
        } else if (data.draftOrderId) {
          // Use the draft order ID to construct checkout URL
          const checkoutUrl = `https://hallyu4u.myshopify.com/${data.draftOrderId}/invoices/latest`;
          console.log(
            "✅ [Payment] Using draft order for checkout:",
            checkoutUrl
          );
          window.open(checkoutUrl, "_blank");
        } else {
          // No invoice exists - notify user
          alert(
            `Invoice not yet created for order ${orderId}. Please contact support or wait for the invoice to be generated.`
          );
          console.log(
            "⚠️ [Payment] No invoice found in admin for order:",
            orderId
          );
        }
      } else {
        // API error - notify user
        alert(
          `Unable to retrieve invoice for order ${orderId}. Please contact support for assistance.`
        );
        console.error("❌ [Payment] Failed to get invoice from admin");
      }
    }
  } catch (error) {
    console.error("❌ [Payment] Error:", error);
    alert(
      `Error processing payment for order ${orderId}. Please contact support for assistance.`
    );
  } finally {
    button.innerHTML = originalContent;
    button.disabled = false;
  }
};


// Get or generate customer code
async function getCustomerCode(customerId, country) {
  if (customerCodeCache) {
    return customerCodeCache;
  }

  try {
    const apiResult = await window.Hallyu4UAPI.customerCode.generate({
      customerId,
    });

    if (!apiResult.success) {
      console.warn("Failed to generate customer code:", apiResult.error);
      return null;
    }

    const responseData = apiResult.data;
    const responseText =
      typeof responseData === "string"
        ? responseData
        : JSON.stringify(responseData);

    if (responseText.trim().startsWith("<")) {
      console.warn("Received HTML instead of JSON for customer code");
      return null;
    }

    const data =
      typeof responseData === "object"
        ? responseData
        : JSON.parse(responseText);
    if (data.success || data.data?.customerCode) {
      const customerCode = data.data?.customerCode || data.customerCode;
      customerCodeCache = customerCode;
      return customerCode;
    }
  } catch (error) {
    console.error("Error getting customer code:", error);
  }
  return null;
}

// You Buy We Ship Handler
function initYouBuyWeShip() {
  const form = document.getElementById("you-buy-form");
  if (!form) return;

  form.addEventListener("submit", async function (e) {
    e.preventDefault();

    // Check if image upload is in progress or required image is missing
    const imageContainers = form.querySelectorAll('.image-upload-container');
    for (const container of imageContainers) {
      if (container.dataset.uploading === 'true') {
        alert('Please wait for image upload to complete.');
        return;
      }
      if (container.dataset.required === 'true') {
        const urlInput = container.querySelector('input[type="hidden"]');
        if (!urlInput || !urlInput.value) {
          alert('Please upload the required product image.');
          return;
        }
      }
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    const btnText = submitBtn.querySelector(".btn-text");
    const btnLoading = submitBtn.querySelector(".btn-loading");

    // Show loading state
    if (btnText) btnText.style.display = "none";
    if (btnLoading) btnLoading.style.display = "inline-flex";
    submitBtn.disabled = true;

    try {
      const formData = new FormData(form);

      // Build items array according to API spec
      const item = {
        productUrl: formData.get("productUrl") || "",
        productImageUrl: formData.get("productImageUrl") || "",
        productName: formData.get("productName") || "",
        productPrice: formData.get("productPrice")
          ? parseFloat(formData.get("productPrice"))
          : 0,
        priceCurrency: formData.get("priceCurrency") || "USD",
        content: formData.get("packageContents") || "",
        quantity: parseInt(formData.get("quantity")) || 1,
        size: formData.get("size") || "",
        color: formData.get("color") || "",
      };

      const orderData = {
        orderType: "YOU_BUY",
        trackingNumber: formData.get("trackingNumber") || "",
        carrier: formData.get("carrier") || "",
        items: [item],
      };

      // Get auth token
      const authToken =
        localStorage.getItem("h4u_token") ||
        sessionStorage.getItem("h4u_token") ||
        localStorage.getItem("customerAccessToken") ||
        null;

      let result = null;
      try {
        const apiResult = await window.Hallyu4UAPI.orders.submitYouBuy(
          orderData
        );

        if (apiResult.success || apiResult.status === 200) {
          const responseData = apiResult.data;
          try {
            result =
              typeof responseData === "object"
                ? responseData
                : JSON.parse(responseData);
          } catch (parseError) {
            result = { success: true, data: responseData };
          }
        }
      } catch (error) {
        let errorMessage = "Failed to create order";
        if (error.response) {
          try {
            const errorText = error.response.data;
            if (errorText) {
              try {
                const errorJson =
                  typeof errorText === "string"
                    ? JSON.parse(errorText)
                    : errorText;
                errorMessage =
                  errorJson.message || errorJson.error || errorMessage;
              } catch (e) {
                errorMessage = errorText || errorMessage;
              }
            }
          } catch (e) {
            errorMessage = `Server returned status ${
              error.response?.status || "unknown"
            }`;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        throw new Error(errorMessage);
      }

      // Check if result indicates success
      // Handle both new API format (ResponseDto with code: 'OK') and legacy format (success: true)
      const isSuccess =
        result &&
        (result.code === "OK" ||
          result.success === true ||
          (result.data && (result.data.orderId || result.data.id)));

      if (isSuccess) {
        // if (result.warehouseAddress && Object.keys(result.warehouseAddress).length > 0) {
        //     displayWarehouseAddress(result.warehouseAddress, result.customerCode);
        // }

        const successMessage = document.getElementById("form-success");
        const errorMessage = document.getElementById("form-error");
        const topButtons = document.querySelector(".top-action-buttons");
        const addressCard = document.getElementById("warehouse-address-card");
        const formCard = form.closest(".warehouse-address-card");

        if (topButtons) topButtons.style.display = "none";
        if (addressCard) {
          addressCard.classList.remove("show");
          addressCard.classList.add("hide");
          addressCard.style.display = "none";
        }
        if (formCard) formCard.style.display = "none";
        form.style.display = "none";
        if (errorMessage) errorMessage.style.display = "none";

        // Show success message
        if (successMessage) {
          successMessage.style.display = "block";

          const orderId = result?.data?.orderId;

          const requestIdElement = document.getElementById("request-id");
          if (requestIdElement) {
            if (orderId) {
              requestIdElement.textContent = orderId;
            } else {
              requestIdElement.textContent = "YBWS-" + Date.now();
            }
            console.log(
              "[You Buy] ✅ Set request-id to:",
              requestIdElement.textContent
            );
          }

          // 홍대 매장 접수(carrier=hongdae): 고객 대시보드 주문 링크 QR + 접수 정보를 보여준다.
          // 직원이 고객 화면의 QR/주문번호로 어드민에서 주문을 찾고, 물건과 신청 내용을 더블체크한다.
          const isHongdae = orderData.carrier === "hongdae";
          const numericId = result?.data?.id;
          if (isHongdae && numericId) {
            renderYouBuyStoreReceipt(successMessage, {
              numericId: numericId,
              orderId: orderId || "",
              warehouseId: result?.data?.warehouseId || "",
            });
          }

          successMessage.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }

        // Track event (optional - only if Hallyu4U global object exists)
        if (window.Hallyu4U && typeof window.Hallyu4U.trigger === "function") {
          window.Hallyu4U.trigger("youbuy:submitted", result);
        }

        showNotification("Service activated successfully!", "success");

        // Reset button state (though form will be hidden)
        if (btnText) btnText.style.display = "";
        if (btnLoading) btnLoading.style.display = "none";
        submitBtn.disabled = false;
      } else {
        // result가 null이거나 실패한 경우
        const errorMsg =
          (result && result.error) ||
          (result && result.message) ||
          "Failed to create order";
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error("Error:", error);

      // Show error message UI
      const successMessage = document.getElementById("form-success");
      const errorMessage = document.getElementById("form-error");
      const topButtons = document.querySelector(".top-action-buttons");
      const addressCard = document.getElementById("warehouse-address-card");
      const formCard = form.closest(".warehouse-address-card");

      // Hide form and other elements
      if (topButtons) topButtons.style.display = "none";
      if (addressCard) {
        addressCard.classList.remove("show");
        addressCard.classList.add("hide");
        addressCard.style.display = "none";
      }
      if (formCard) formCard.style.display = "none";
      if (successMessage) successMessage.style.display = "none";

      // Show error message
      if (errorMessage) {
        const errorTextElement = document.getElementById("error-text");
        if (errorTextElement) {
          errorTextElement.textContent =
            error.message || "An error occurred. Please try again.";
        }
        errorMessage.style.display = "block";
        errorMessage.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      showNotification("Error: " + error.message, "error");

      // Reset button state
      if (btnText) btnText.style.display = "";
      if (btnLoading) btnLoading.style.display = "none";
      submitBtn.disabled = false;
    }
  });
}

/**
 * 홍대 매장 접수 영수증 영역: 대시보드 주문 링크 QR + 주문번호/이름/창고ID/일시.
 * QR 라이브러리(qrcode.min.js, 섹션에서 defer 로드)가 없으면 링크만 보여준다.
 */
function renderYouBuyStoreReceipt(successMessage, info) {
  const box = document.getElementById("yb-success-store");
  if (!box) return;
  const dashboardUrl =
    window.location.origin +
    "/pages/dashboard?tab=orders&order=" +
    encodeURIComponent(String(info.numericId));

  const setText = (id, text) => {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  };
  setText("yb-success-order-id", info.orderId || String(info.numericId));
  setText("yb-success-name", (successMessage && successMessage.dataset.customerName) || "");
  setText("yb-success-warehouse-id", info.warehouseId || "");
  setText("yb-success-date", new Date().toLocaleString());

  const link = document.getElementById("yb-success-link");
  if (link) {
    link.href = dashboardUrl;
    link.textContent = dashboardUrl;
  }

  const qrEl = document.getElementById("yb-success-qr");
  if (qrEl) {
    qrEl.innerHTML = "";
    if (typeof window.QRCode === "function") {
      try {
        new window.QRCode(qrEl, {
          text: dashboardUrl,
          width: 180,
          height: 180,
          correctLevel: window.QRCode.CorrectLevel ? window.QRCode.CorrectLevel.M : undefined,
        });
      } catch (e) {
        console.warn("[You Buy] QR render failed", e);
      }
    }
  }

  const defaultNote = document.getElementById("yb-success-default-note");
  if (defaultNote) defaultNote.style.display = "none";
  box.style.display = "block";
}

// Shipping Calculator
function initShippingCalculator() {
  const calcForm = document.getElementById("shipping-calc-form");
  if (!calcForm) return;

  calcForm.addEventListener("submit", async function (e) {
    e.preventDefault();

    const weight = parseFloat(document.getElementById("calc-weight").value);
    const country = document.getElementById("calc-country").value;

    try {
      const apiResult = await window.Hallyu4UAPI.shipping.calculate({
        weight,
        destination: country,
      });

      if (!apiResult.success) {
        throw new Error(apiResult.error || "Failed to calculate shipping");
      }

      const responseData = apiResult.data;
      const responseText =
        typeof responseData === "string"
          ? responseData
          : JSON.stringify(responseData);

      if (responseText.trim().startsWith("<")) {
        throw new Error("Server returned HTML instead of shipping data");
      }

      const data =
        typeof responseData === "object"
          ? responseData
          : JSON.parse(responseText);
      displayShippingOptions(data.options || data.data?.options || []);
    } catch (error) {
      console.error("Error calculating shipping:", error);
      showNotification("Failed to calculate shipping", "error");
    }
  });
}

// Display shipping options
function displayShippingOptions(options) {
  const container = document.getElementById("shipping-options");
  if (!container) return;

  container.innerHTML = options
    .map(
      (option) => `
        <div class="shipping-option">
            <div class="option-header">
                <h4>${option.name}</h4>
                <span class="delivery-time">${option.deliveryTime}</span>
            </div>
            <div class="option-price">
                <span class="price-krw">₩${option.price.toLocaleString()}</span>
                <span class="price-usd">≈ $${(option.price / 1400).toFixed(
                  2
                )}</span>
            </div>
            <button onclick="selectShippingOption('${
              option.method
            }')" class="select-btn">
                Select This Option
            </button>
        </div>
    `
    )
    .join("");
}

// Customer Dashboard Integration
async function loadCustomerDashboard() {
  if (!window.customerId) return;

  try {
    if (!window.Hallyu4UAPI) {
      console.warn("API Store not available");
      return;
    }

    const apiResult = await window.Hallyu4UAPI.request(
      `/customers/shopify/${window.customerId}`,
      {
        method: "GET",
        key: "customers.shopify",
      }
    );

    if (!apiResult.success) {
      console.error("Failed to load dashboard:", apiResult.error);
      return;
    }

    const responseData = apiResult.data;
    const responseText =
      typeof responseData === "string"
        ? responseData
        : JSON.stringify(responseData);

    if (responseText.trim().startsWith("<")) {
      console.warn("Received HTML instead of JSON for dashboard");
      return;
    }

    const data =
      typeof responseData === "object"
        ? responseData
        : JSON.parse(responseText);

    updateDashboardStats(data);
    loadCustomerPackages(data.packages);
    loadCustomerOrders(data.orders);
  } catch (error) {
    console.error("Error loading dashboard:", error);
  }
}

// Update dashboard statistics
function updateDashboardStats(data) {
  const elements = {
    totalPackages: document.getElementById("total-packages"),
    inStorage: document.getElementById("in-storage"),
    totalSaved: document.getElementById("total-saved"),
    memberLevel: document.getElementById("member-level"),
  };

  if (elements.totalPackages)
    elements.totalPackages.textContent = data.packages?.length || 0;
  if (elements.inStorage)
    elements.inStorage.textContent = data.packagesInStorage || 0;
  if (elements.totalSaved)
    elements.totalSaved.textContent = `₩${(
      data.totalSaved || 0
    ).toLocaleString()}`;
  if (elements.memberLevel)
    elements.memberLevel.textContent = data.memberLevel || "Bronze";
}

// Load customer packages
function loadCustomerPackages(packages) {
  const packageList = document.getElementById("package-list");
  if (!packageList || !packages) return;

  packageList.innerHTML = "";

  packages.forEach((pkg) => {
    const packageCard = document.createElement("div");
    packageCard.className = "package-card";
    packageCard.innerHTML = `
            <div class="package-header">
                <h3>${pkg.trackingNumber}</h3>
                <span class="status-badge status-${pkg.status}">${
      pkg.status
    }</span>
            </div>
            <div class="package-details">
                <p><strong>Carrier:</strong> ${pkg.carrier}</p>
                <p><strong>Weight:</strong> ${pkg.weight}kg</p>
                <p><strong>Arrival Date:</strong> ${window.ThemeUtils.toLocalTime(
                  pkg.arrivalDate
                )}</p>
            </div>
        `;
    packageList.appendChild(packageCard);
  });
}

// Load customer orders
function loadCustomerOrders(orders) {
  const orderList = document.getElementById("order-list");
  if (!orderList || !orders) return;

  orderList.innerHTML = "";

  orders.forEach((order) => {
    const orderCard = document.createElement("div");
    orderCard.className = "order-card";
    orderCard.innerHTML = `
            <div class="order-header">
                <h3>Order #${order.orderNumber}</h3>
                <span class="status-badge status-${order.status}">${
      order.status
    }</span>
            </div>
            <div class="order-details">
                <p><strong>Date:</strong> ${window.ThemeUtils.toLocalTime(
                  order.createdAt
                )}</p>
                <p><strong>Total:</strong> ₩${order.total.toLocaleString()}</p>
                <p><strong>Items:</strong> ${order.items?.length || 0}</p>
            </div>
        `;
    orderList.appendChild(orderCard);
  });
}

// Display tracking information
function displayTrackingInfo(data) {
  const trackingContainer = document.getElementById("tracking-result");
  if (!trackingContainer) return;

  trackingContainer.innerHTML = `
        <div class="tracking-info">
            <h3>Tracking: ${data.trackingNumber}</h3>
            <div class="tracking-status">
                <span class="status-badge status-${data.status}">${
    data.status
  }</span>
            </div>
            <div class="tracking-details">
                <p><strong>Carrier:</strong> ${data.carrier}</p>
                <p><strong>Current Location:</strong> ${
                  data.currentLocation || "N/A"
                }</p>
                <p><strong>Estimated Delivery:</strong> ${
                  data.estimatedDelivery
                    ? window.ThemeUtils.toLocalTime(data.estimatedDelivery)
                    : "N/A"
                }</p>
            </div>
            <div class="tracking-timeline">
                <h4>Tracking History</h4>
                ${
                  data.trackingEvents
                    ?.map(
                      (event) => `
                    <div class="tracking-event">
                        <span class="event-date">${new Date(
                          event.date
                        ).toLocaleString()}</span>
                        <span class="event-description">${
                          event.description
                        }</span>
                        <span class="event-location">${
                          event.location || ""
                        }</span>
                    </div>
                `
                    )
                    .join("") || "<p>No tracking events available</p>"
                }
            </div>
        </div>
    `;
}

// Package tracking
async function trackPackage(trackingNumber) {
  try {
    const apiResult = await window.Hallyu4UAPI.packages.track(trackingNumber);

    if (!apiResult.success) {
      throw new Error(apiResult.error || "Failed to track package");
    }

    const responseData = apiResult.data;
    const responseText =
      typeof responseData === "string"
        ? responseData
        : JSON.stringify(responseData);

    if (responseText.trim().startsWith("<")) {
      throw new Error("Server returned HTML instead of tracking data");
    }

    const data =
      typeof responseData === "object"
        ? responseData
        : JSON.parse(responseText);
    displayTrackingInfo(data);
  } catch (error) {
    console.error("Error tracking package:", error);
    showNotification("Failed to track package", "error");
  }
}

// Notification system
function showNotification(message, type = "info") {
  const notification = document.createElement("div");
  notification.className = `notification notification-${type}`;
  notification.textContent = message;

  document.body.appendChild(notification);

  setTimeout(() => {
    notification.classList.add("show");
  }, 100);

  setTimeout(() => {
    notification.classList.remove("show");
    setTimeout(() => notification.remove(), 300);
  }, 5000);
}

// Initialize on page load
document.addEventListener("DOMContentLoaded", function () {
  // Get customer info from Shopify
  if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta.page.customerId) {
    window.customerId = window.ShopifyAnalytics.meta.page.customerId;
  }

  // Initialize based on current page
  const currentPage = window.location.pathname;

  if (currentPage.includes("we-buy-we-ship")) {
    initWeBuyWeShip();
    initPriceCalculator();
  } else if (currentPage.includes("you-buy-we-ship")) {
    initYouBuyWeShip();
  } else if (currentPage.includes("shipping-calculator")) {
    initShippingCalculator();
  } else if (
    currentPage.includes("dashboard") ||
    currentPage.includes("account")
  ) {
    loadCustomerDashboard();
  }

  // Initialize package tracking form
  const trackingForm = document.getElementById("tracking-form");
  if (trackingForm) {
    trackingForm.addEventListener("submit", function (e) {
      e.preventDefault();
      const trackingNumber = document.getElementById("tracking-number").value;
      trackPackage(trackingNumber);
    });
  }
});

// Price calculator for We Buy We Ship
function initPriceCalculator() {
  const inputs = ["product-price", "product-weight", "destination-country"];

  inputs.forEach((id) => {
    const element = document.getElementById(id);
    if (element) {
      element.addEventListener("change", calculateWeBuyPrice);
    }
  });
}

// Calculate We Buy We Ship price
function calculateWeBuyPrice() {
  const price = parseFloat(
    document.getElementById("product-price")?.value || 0
  );
  const weight = parseFloat(
    document.getElementById("product-weight")?.value || 0
  );
  const country = document.getElementById("destination-country")?.value;

  if (!price || !weight || !country) return;

  // Calculate fees
  const serviceFee = price * 0.05;
  const domesticShipping = 3000;

  // Get shipping rate based on actual data
  const shippingRates = {
    us: { dhl: 31637, fedex: 23851, ems: 27113, kpacket: 0 },
    jp: { dhl: 24965, fedex: 22720, ems: 23550, kpacket: 4590 },
    cn: { dhl: 25084, fedex: 25184, ems: 23550, kpacket: 0 },
    eu: { dhl: 28174, fedex: 26141, ems: 32723, kpacket: 0 },
  };

  const countryRates = shippingRates[country] || shippingRates.us;
  const internationalShipping =
    weight * (countryRates.dhl / (weight > 0.5 ? 1 : 2));

  const total = price + serviceFee + domesticShipping + internationalShipping;

  // Update display
  document.getElementById(
    "calc-product-price"
  ).textContent = `₩${price.toLocaleString()}`;
  document.getElementById(
    "calc-service-fee"
  ).textContent = `₩${serviceFee.toLocaleString()}`;
  document.getElementById(
    "calc-domestic-shipping"
  ).textContent = `₩${domesticShipping.toLocaleString()}`;
  document.getElementById(
    "calc-international-shipping"
  ).textContent = `₩${Math.round(internationalShipping).toLocaleString()}`;
  document.getElementById("calc-total").textContent = `₩${Math.round(
    total
  ).toLocaleString()}`;

  const resultDiv = document.getElementById("price-result");
  if (resultDiv) {
    resultDiv.classList.remove("hidden");
  }
}

// Copy warehouse address
function copyAddress(customerCode) {
  const addressDetails = document.querySelector(".address-details");
  if (!addressDetails) return;

  let addressText = "";
  const spans = addressDetails.querySelectorAll("span");
  spans.forEach((span, index) => {
    if (index === 0) addressText += `Recipient: ${span.textContent}\n`;
    else if (index === 1) addressText += `Address: ${span.textContent}\n`;
    else if (index === 2) addressText += `Building: ${span.textContent}\n`;
    else if (index === 3) addressText += `City: ${span.textContent}\n`;
    else if (index === 4) addressText += `Postal Code: ${span.textContent}\n`;
    else if (index === 5) addressText += `Country: ${span.textContent}\n`;
    else if (index === 6) addressText += `Phone: ${span.textContent}\n`;
  });

  addressText += `\n⚠️ IMPORTANT: Write "${customerCode}" on your package!\n`;

  if (addressText) {
    navigator.clipboard
      .writeText(addressText)
      .then(() => {
        showNotification("Full address copied to clipboard!", "success");
      })
      .catch(() => {
        showNotification("Failed to copy address", "error");
      });
  }
}

// Copy customer code only
function copyCustomerCode(code) {
  if (code) {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        showNotification(`Customer code "${code}" copied!`, "success");
      })
      .catch(() => {
        showNotification("Failed to copy code", "error");
      });
  }
}

// Export functions for global use
window.HALLYU4U = {
  api: HALLYU4U_API,
  trackPackage,
  calculateWeBuyPrice,
  showNotification,
  copyAddress,
  copyCustomerCode,
  getCustomerCode,
  // displayWarehouseAddress
};
